import React from "react";
import {
  Mail, Send, CheckCircle2, XCircle, RefreshCw, Loader2, AlertCircle, Shield, Server,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("da-DK", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function MailRoutingPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [testing, setTesting] = React.useState(null);
  const [overrides, setOverrides] = React.useState({});

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await adminApi().get("/admin/email-routing-status");
      setData(r);
    } catch (e) {
      toast.error("Kunne ikke hente mail-routing");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const sendTest = async (purpose) => {
    setTesting(purpose);
    try {
      const target_override = (overrides[purpose] || "").trim() || undefined;
      const { data: r } = await adminApi().post("/admin/email-routing-status/test", {
        purpose, target_override,
      });
      if (r.ok) {
        toast.success(`Test-mail sendt til ${r.target}`);
      } else {
        toast.error(`Test fejlede: ${r.error || "ukendt fejl"}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende test-mail");
    } finally { setTesting(null); }
  };

  if (loading && !data) {
    return <div className="bg-white border border-brand p-6 text-sm text-brand-text2" data-testid="mail-routing-loading">Indlæser…</div>;
  }
  if (!data) return null;

  const smtpOk = data.smtp?.configured;

  return (
    <div className="space-y-4" data-testid="mail-routing-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl text-brand-dark flex items-center gap-2">
            <Mail size={22} className="text-brand-red" />
            Mail-routing
          </h2>
          <p className="text-xs text-brand-text2 mt-1">
            Verificér at hver indbakke modtager mail. Send en test-mail til formålet — eller en alternativ adresse.
          </p>
        </div>
        <button
          onClick={load}
          className="text-xs border border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white px-3 py-2 flex items-center gap-1 transition-colors flex-shrink-0"
          data-testid="mail-routing-refresh"
        >
          <RefreshCw size={12} /> Opdater
        </button>
      </div>

      {/* SMTP status */}
      <div className={`border p-4 flex items-start gap-3 ${smtpOk ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"}`} data-testid="smtp-status">
        <Server size={18} className={smtpOk ? "text-emerald-700" : "text-red-700"} />
        <div className="flex-1 text-sm">
          <div className={`font-bold ${smtpOk ? "text-emerald-900" : "text-red-900"}`}>
            SMTP: {smtpOk ? "konfigureret og klar" : "IKKE konfigureret"}
          </div>
          <div className="text-xs text-brand-text2 mt-1">
            <span className="font-mono">{data.smtp.user || "—"}</span> ·
            <span className="font-mono ml-1">{data.smtp.host || "—"}:{data.smtp.port || "—"}</span>
          </div>
        </div>
      </div>

      {/* Mailbox table */}
      <div className="bg-white border border-brand overflow-x-auto" data-testid="mailbox-table">
        <table className="min-w-full text-sm">
          <thead className="bg-brand-dark text-white">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Formål</th>
              <th className="text-left px-3 py-2 font-semibold">Indbakke</th>
              <th className="text-center px-3 py-2 font-semibold">Env</th>
              <th className="text-left px-3 py-2 font-semibold">Sidste test</th>
              <th className="text-left px-3 py-2 font-semibold">Alternativ modtager (valgfri)</th>
              <th className="text-right px-3 py-2 font-semibold">Handling</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand">
            {data.mailboxes.map((m) => {
              const status = m.last_test_status;
              return (
                <tr key={m.purpose} className="hover:bg-brand-bg/40" data-testid={`mailbox-row-${m.purpose}`}>
                  <td className="px-3 py-2">
                    <div className="font-bold text-brand-dark flex items-center gap-1.5">
                      {m.label}
                      {m.is_whistleblower && (
                        <span className="text-[10px] bg-amber-100 text-amber-900 border border-amber-300 px-1.5 py-0.5 uppercase font-bold flex items-center gap-1" title="Whistleblower-adressen bruges KUN til at modtage — aldrig som outgoing From">
                          <Shield size={10} /> Kun modtag
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-brand-text2 mt-0.5">
                      {m.is_whistleblower ? "(ingen outgoing From)" : <>"{m.from_name}"</>}
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-brand-dark">{m.inbox}</td>
                  <td className="px-3 py-2 text-center">
                    {m.env_var_set ? (
                      <CheckCircle2 size={14} className="text-emerald-700 inline" title={m.env_var} />
                    ) : (
                      <XCircle size={14} className="text-red-700 inline" title={`${m.env_var} MANGLER`} />
                    )}
                    <div className="text-[10px] font-mono text-brand-text2 mt-0.5">{m.env_var}</div>
                  </td>
                  <td className="px-3 py-2">
                    {m.last_test_at ? (
                      <div className="flex items-center gap-1.5 text-xs">
                        {status === "success" ? (
                          <CheckCircle2 size={12} className="text-emerald-700 flex-shrink-0" />
                        ) : (
                          <AlertCircle size={12} className="text-red-700 flex-shrink-0" />
                        )}
                        <div>
                          <div className={status === "success" ? "text-emerald-700 font-bold" : "text-red-700 font-bold"}>
                            {status === "success" ? "OK" : "Fejl"}
                          </div>
                          <div className="text-[10px] text-brand-text2">{fmt(m.last_test_at)}</div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-brand-text2 italic">aldrig testet</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="email"
                      value={overrides[m.purpose] || ""}
                      onChange={(e) => setOverrides((o) => ({ ...o, [m.purpose]: e.target.value }))}
                      placeholder="(send til formålet)"
                      className="w-full border border-brand px-2 py-1.5 text-xs focus:outline-none focus:border-brand-dark font-mono"
                      data-testid={`mailbox-override-${m.purpose}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => sendTest(m.purpose)}
                      disabled={testing !== null || !smtpOk}
                      className="text-xs bg-brand-red text-white hover:bg-red-700 px-3 py-2 flex items-center justify-center gap-1 transition-colors disabled:opacity-60 ml-auto"
                      data-testid={`mailbox-test-${m.purpose}`}
                    >
                      {testing === m.purpose ? (
                        <Loader2 size={11} className="animate-spin" />
                      ) : (
                        <Send size={11} />
                      )}
                      Test
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900">
        <b>Tip:</b> Test-mailen sendes med præcis det From + Reply-To som dine kunder ville se. Tjek selve indbakken
        (eller fx en alternativ adresse i feltet "Alternativ modtager") for at bekræfte at den faktisk lander.
        Hvis en boks fejler — kontakt Simply.com support for at oprette alias/mailbox eller justere fremsendelse.
      </div>
    </div>
  );
}
